import React, { useContext } from 'react';
import { compose } from 'redux';
import { withTranslation, useTranslation } from 'react-i18next';
import { ALL_FOLDER_ID } from '@wix/da-url';
import shortenNumber from '@wix/da-shared-react/pkg/utils/shortenNumber';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import PremiumFolderDataIcon from '@wix/da-shared-react/pkg/GallectionFolderDropdown/_partials/PremiumFolderDataIcon';
import { normalizePremiumFolderData } from '@wix/da-shared-react/pkg/utils/normalizePremiumFolderData';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import SortOrderSwitcher from '../SortOrderSwitcher';
import FolderBarFolderDropdown from './_partials/FolderBarFolderDropdown';
import { PapiGallection, PapiUser } from '@wix/da-papi-types';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import {
  withMobileContext,
  MobileContext,
} from '@wix/da-react-context/pkg/MobileContext';
import { pointsToDollars } from '@wix/da-shared-react/pkg/utils/points';
import TextTooltipOnHover from '@wix/da-shared-react/pkg/Popover/Preset/TextTooltipOnHover';
import InfoIcon from '@wix/da-ds/pkg/Icons/24x24/Info';
import EditGallectionButton from '../EditGallectionButton';

import s from './FolderBar.scss';

export interface Props {
  currentFolder?: PapiGallection | null;
  profileOwner?: PapiUser | null;
  isCurrentUserProfileOwner?: boolean;
  unsetSelectedFolderId: () => void;
  withBackButton?: boolean;
  profitMargin: number;
}

const enhance = compose(withMobileContext, withTranslation());

export const FolderBar: React.FC<Props> = ({
  currentFolder,
  profileOwner,
  profitMargin,
  isCurrentUserProfileOwner,
  withBackButton,
  unsetSelectedFolderId,
}) => {
  const isEarningsCalculator = useFlag('earnings_calculator');
  const isMobile = useContext(MobileContext);
  const { t } = useTranslation();

  if (!currentFolder || !profileOwner) {
    return null;
  }

  const { isPaidFolder, numSubscribers } = normalizePremiumFolderData(
    currentFolder?.premiumFolderData
  );

  let artistPointsProfit;
  if (isPaidFolder) {
    artistPointsProfit = Math.floor(
      currentFolder!.premiumFolderData!.pointsPrice! * (1 - profitMargin)
    );
  }

  const vespaSearchEngineIsReady = false;

  const artistPointsBlock = (
    <>
      <span className={s['earnings-value']}>
        ${pointsToDollars(artistPointsProfit)}
      </span>
      {' / '}
      <span className={s['earnings-value']}>
        {artistPointsProfit}
        &nbsp;
        {t('common.points', { count: artistPointsProfit })}
      </span>
    </>
  );

  return (
    <>
      <div className={s['folder-info']}>
        {withBackButton && (
          <PlainButtonOrLink
            className={s['back']}
            onClick={unsetSelectedFolderId}
          >
            {t!('pages.gallections.back')}
          </PlainButtonOrLink>
        )}
        <PremiumFolderDataIcon
          premiumFolderData={currentFolder?.premiumFolderData}
          size={IconSize.COMPACT}
          className={s['premium-folder-icon']}
        />
        <FolderBarFolderDropdown currentFolder={currentFolder} />
        {isPaidFolder && (
          <div className={s['comments-count']}>
            {t!('common.supporters_plural')}
            <span className={s['count']}>{shortenNumber(numSubscribers)}</span>
          </div>
        )}
      </div>
      <div className={s['right-zone']}>
        <div>
          {!isMobile && currentFolder!.folderId !== ALL_FOLDER_ID && (
            <EditGallectionButton gallection={currentFolder} />
          )}
          {vespaSearchEngineIsReady && (
            <SortOrderSwitcher className={s['sort-order-switch']} />
          )}
        </div>
        {!isMobile &&
          isPaidFolder &&
          isCurrentUserProfileOwner &&
          isEarningsCalculator && (
            <div className={s['earnings-line']}>
              {t('common.pointsInput.yourEarnings')}: {artistPointsBlock}{' '}
              <TextTooltipOnHover
                placement="top"
                className={s['earnings-icon']}
                tooltipText={t('common.pointsInput.feesTooltip')}
              >
                <InfoIcon size={IconSize.COMPACT} />
              </TextTooltipOnHover>
            </div>
          )}
      </div>
    </>
  );
};
FolderBar.displayName = 'FolderBar';

export default enhance(FolderBar) as React.ComponentType<Props>;
